import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { BudgetAllocation } from '@/components/DataTable/CellContent/BudgetAllocation'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import {
  resolveComparison,
  useBudgetAllocationVariance,
  type BudgetAllocationVariance,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import { VARIANCE_GROUPING_DIMENSION_TYPES } from '@repo/constants'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { ExternalLinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CRT_ROUTES, toStaticData } from '../crt-route-config'
import { DrillDataTable } from './-components/DrillDataTable'
import { DrillPageHeader } from './-components/DrillPageHeader'
import { type DrillRow } from './-data/drill-dimensions'
import {
  decodeDrillValue,
  drillSearchSchema,
  requireComparisonSides,
  toCostTypeCellScope,
} from './-data/drill-search'

export const Route = createFileRoute('/_auth/_modules/crt/variance-analysis/by-budget')({
  staticData: toStaticData(CRT_ROUTES['/crt/variance-analysis/by-budget']),
  component: RouteComponent,
  validateSearch: (search) => drillSearchSchema.parse(search),
  beforeLoad: ({ search }) => requireComparisonSides(search),
})

/**
 * The endpoint's rows as the drill table reads them.
 *
 * Only a rename and a decode: the endpoint's grouping keys are already the drill vocabulary's names,
 * and the one translation needed is `NONE_VALUE` back to `null`, which is what the budget-allocation
 * and program cells render their "absent" reading from. The endpoint carries the sentinel because a
 * URL cannot carry a `null`; the table has no such constraint.
 *
 * `investmentType` is not projected. It keys the rows on the server — two programs of different types on
 * one budget stay apart because of it — but nothing on the page reads it. `manualInvestmentId` keys them
 * the same way and *is* projected, without a column of its own: it is what the program cell links by.
 */
function toBudgetAllocationDrillRows(data: BudgetAllocationVariance | undefined): DrillRow[] {
  if (!data) return []

  return data.results.map((row) => ({
    grouping: {
      budgetCategory: row.grouping.budgetCategory,
      // Read from the RAP import rather than derived from the code, so it is null for every bucket that
      // import does not forecast — which is most of them outside FMC and vapour.
      broaderBucket: row.grouping.broaderBucket,
      budgetAllocation: decodeDrillValue(row.grouping.budgetAllocation) ?? null,
      programName: row.grouping.programName,
      // Raw: only `budgetAllocation` carries the sentinel, and the id is already a real `string | null`.
      manualInvestmentId: row.grouping.manualInvestmentId,
    },
    variance: row.variance,
  }))
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // `state: true` keeps the drill history (`drillFrom`) on the entry, as the header's own navigations do.

  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true, state: true }),
  })

  // The comparison, the heat-map cell and the cost-type cell all come out of the search: the header
  // configured the two sides, the cell named the material scope, and the row the reader drilled named
  // which cost type and source these figures explain.
  const comparison = resolveComparison(search)
  const scope = toCostTypeCellScope(search)

  const { data, isPending, isPlaceholderData, error } = useBudgetAllocationVariance(
    comparison,
    scope,
  )
  const rows = toBudgetAllocationDrillRows(data)

  /** Why the table has nothing to show, when it has nothing to show. */
  const unavailableError = !comparison
    ? new Error(t('varianceAnalysis.emptyState'))
    : !scope
      ? new Error(t('varianceAnalysis.drill.byBudget.unavailableScope'))
      : null

  const leadingColumns: ColumnDef<DrillRow, unknown>[] = [
    {
      id: 'budgetCategory',
      // Sorted on the label rather than the code: the two orders agree in English and part ways in
      // French, and a column has to sort by what the reader is looking at.
      accessorFn: (row) =>
        row.grouping.budgetCategory
          ? t(`budgetTracker.filters.budgetCategory.${row.grouping.budgetCategory}`)
          : undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      // The tracker's own label for the same idea, rather than a second key saying the same thing.
      meta: { displayTitle: t('budgetTracker.filters.budgetCategory') },
      cell: ({ row }) => {
        const budgetCategory = row.original.grouping.budgetCategory
        // Derived from the code's prefix, so a category is missing exactly when the code is — an
        // unattributed row, which the allocation cell says in full.
        if (!budgetCategory) return <NoDataAvailable />

        return (
          <Text tone="muted">{t(`budgetTracker.filters.budgetCategory.${budgetCategory}`)}</Text>
        )
      },
    },
    {
      id: 'budgetAllocation',
      accessorFn: (row) => row.grouping.budgetAllocation ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.budgetAllocation') },
      cell: ({ row }) => {
        const budgetAllocation = row.original.grouping.budgetAllocation
        if (!budgetAllocation) return <NoDataAvailable />

        return (
          <HStack gap="xs" align="center">
            <BudgetAllocation id={budgetAllocation} />
            {row.original.labels?.budgetAllocation && (
              <Text size="xs" tone="muted" maxLines="one">
                {row.original.labels.budgetAllocation}
              </Text>
            )}
          </HStack>
        )
      },
    },
    {
      id: 'broaderBucket',
      accessorFn: (row) => row.grouping.broaderBucket ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.broaderBucket') },
      cell: ({ row }) => {
        const broaderBucket = row.original.grouping.broaderBucket
        if (!broaderBucket) return <NoDataAvailable />

        return <Text tone="muted">{broaderBucket}</Text>
      },
    },
    {
      id: 'programName',
      accessorFn: (row) => row.grouping.programName ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.programName') },
      cell: ({ row }) => {
        const { programName, manualInvestmentId } = row.original.grouping

        if (!programName) return <NoDataAvailable />

        return (
          <HStack gap="xs" align="center">
            <Text>{programName}</Text>
            {manualInvestmentId && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/crt/customer-investments/insights/manual-investments/$manualInvestmentId"
                      params={{ manualInvestmentId }}
                      aria-label={t('varianceAnalysis.drill.actions.viewManualInvestment')}
                      className="text-muted-foreground hover:text-foreground inline-flex transition-colors"
                    >
                      <ExternalLinkIcon className="size-4" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('varianceAnalysis.drill.actions.viewManualInvestment')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </HStack>
        )
      },
    },
  ]

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" align="stretch">
        <DrillPageHeader
          title={t('varianceAnalysis.drill.byBudget.pageHeader')}
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byBudgetAllocation}
        />

        {/* Flat: a budget allocation and its program are one row, not a hierarchy. */}
        <DrillDataTable
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byBudgetAllocation}
          rows={rows}
          leadingColumns={leadingColumns}
          canHideEmptyRows
          currency={conversion}
          isPending={isPending}
          isRefreshing={isPlaceholderData}
          error={error ?? unavailableError}
          // A budget commitment is spend, so more of it is money going out: the increase reads
          // as unfavourable here, the same way it does on the cost-type table above.
          isIncreaseFavourable={false}
        />
      </VStack>
    </PageContainer>
  )
}
